<template>
  <v-card outlined>
    <v-card-title> Templates </v-card-title>

    <v-card-text>
      <v-form
        ref="formRef"
        class="multi-col-validation"
        @submit.prevent="handleSubmit"
      >
        <v-row>
          <v-col
            md="4"
            cols="12"
          >
            <v-text-field
              v-model="template.nome"
              dense
              outlined
              label="Nome do template"
              validate-on-blur
              hint="Máximo de 50 caracteres"
              :rules="[validators.required, v => (!!v && v.length <= 50) || 'Máximo de 50 caracteres']"
            />
          </v-col>

          <v-col
            md="4"
            cols="12"
          >
            <v-file-input
              ref="templateInputRef"
              dense
              outlined
              show-size
              hide-details
              validate-on-blur
              label="Importar template"
              accept=".txt"
              clear-icon
              :prepend-icon="null"
              :prepend-inner-icon="icons.mdiPaperclip"
              :rules="fileInputRules"
              @change="uploadTemplate"
            />
          </v-col>

          <v-col
            cols="3"
            class="pt-0"
          >
            <v-switch
              v-model="template.ativo"
              hide-details
              :label="`Status: ${template.ativo ? 'Ativo' : 'Inativo'}`"
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col
            cols="12"
            class="d-flex justify-center"
          >
            <v-btn
              color="primary"
              outlined
              class="mr-4"
              @click="$refs.templateExemploRef.click()"
            >
              <v-icon left>
                {{ icons.mdiDownloadCircle }}
              </v-icon>
              <span>Download template de exemplo</span>
              <a
                ref="templateExemploRef"
                download
                target="_blank"
                :href="templateExemploUrl"
              />
            </v-btn>

            <v-btn
              color="primary"
              outlined
              type="submit"
              class="mr-4"
              :loading="loading"
            >
              <v-progress-circular
                v-if="loading"
                indeterminate
                class="mx-2"
              />
              <span v-else>{{ tituloBotaoSubmit }}</span>
            </v-btn>

            <v-btn
              outlined
              color="secondary"
              @click="resetData"
            >
              Cancelar
            </v-btn>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-data-table
              hide-default-footer
              class="elevation-4"
              no-data-text="Nenhum template cadastrado"
              :loading="isGetTemplatesLoading"
              loading-text="Carregando templates"
              :item-class="() => 'cursor-pointer'"
              :headers="headers"
              :items="listaTemplatesExibicao"
              @click:row="selecionarTemplate"
            >
              <template #item.nome="{ item }">
                <span>{{ functions.truncateText(item.nome, 10) }}</span>
              </template>

              <template #item.zenviaTemplateId="{ item }">
                <span>{{ item.zenviaTemplateId ? functions.truncateText(item.zenviaTemplateId, 10) : '-' }}</span>
              </template>

              <template #item.conteudo="{ item }">
                <v-btn
                  icon
                  color="primary"
                  class="ml-2"
                  @click.stop="downloadTemplateSelecionado(item.conteudo)"
                >
                  <v-icon> {{ icons.mdiDownloadCircle }} </v-icon>
                </v-btn>
                <a
                  ref="templateSelecionadoRef"
                  :href="templateSelecionadoUrl"
                  download="Template.txt"
                />
              </template>

              <template #item.dataUltimaAlteracao="{ item }">
                <span>{{ item.dataUltimaAlteracao ? functions.converterDateStringEmLocaleDateString(item.dataUltimaAlteracao) : '-' }}</span>
              </template>

              <template #item.ativo="{ item }">
                <span>{{ item.ativo ? 'Ativo' : 'Inativo' }}</span>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script>
import Vue from 'vue'
import { ref, computed, watch } from '@vue/composition-api'
import { required, select } from '@core/utils/validation'
import { mdiPaperclip, mdiDownloadCircle } from '@mdi/js'
import { converterDateStringEmLocaleDateString } from '@/utils/dateTime'
import CanalWhatsAppTemplateModel from '@/models/entities/canal/CanalWhatsAppTemplateModel'
import CanalService from '@/services/modules/CanalService'
import truncateText from '@/utils/text'
import useForm from '@/composables/useForm'

export default {
  // Novo emit para enviar lista ao pai
  emits: ['update:templates'],

  props: {
    canalWhatsAppId: {
      type: Number,
      default: 0, // Alterado para aceitar 0/null
    },
  },

  setup(props, { emit }) {
    const templatesCadastrados = ref([]) // Vindos da API
    const templatesLocais = ref([]) // Criados em memória
    const isGetTemplatesLoading = ref(false)

    // Identifica se estamos criando um canal novo (sem ID)
    const isModoLocal = computed(() => !props.canalWhatsAppId)

    // Decide qual lista mostrar na tabela
    const listaTemplatesExibicao = computed(() => (isModoLocal.value ? templatesLocais.value : templatesCadastrados.value))

    // --- Lógica de API (Modo Edição) ---
    async function getTemplatesCadastrados() {
      if (!props.canalWhatsAppId) return

      try {
        isGetTemplatesLoading.value = true
        templatesCadastrados.value = await CanalService.getTemplatesWhatsApp(props.canalWhatsAppId)
      } finally {
        isGetTemplatesLoading.value = false
      }
    }

    watch(() => props.canalWhatsAppId, getTemplatesCadastrados, { immediate: true })

    // --- Lógica do Formulário ---
    const { formRef, loading } = useForm()
    const templateInputRef = ref(null)
    const template = ref(new CanalWhatsAppTemplateModel())

    // Controle de edição local (Temp ID)
    const tempIdEdicao = ref(null)

    function selecionarTemplate(t) {
      templateInputRef.value.reset()
      template.value = JSON.parse(JSON.stringify(t))

      // Se for local, guardamos o ID temporário para saber qual atualizar
      if (isModoLocal.value) {
        tempIdEdicao.value = t.tempId
      }
    }

    const isNovoTemplate = computed(() => {
      if (isModoLocal.value) return !tempIdEdicao.value
      return template.value.id === 0
    })

    const tituloBotaoSubmit = computed(() => (isNovoTemplate.value ? 'Adicionar' : 'Atualizar'))

    // Regra de validação ajustada: Se tem conteúdo (edição), arquivo não é obrigatório
    const fileInputRules = computed(() => {
      if (template.value.conteudo) return []
      return [v => !!v || 'Template é obrigatório']
    })

    function uploadTemplate(file) {
      if (!file) return
      const reader = new FileReader()
      reader.readAsText(file, 'UTF-8')
      reader.onload = () => {
        template.value.conteudo = reader.result
      }
    }

    function resetData() {
      templateInputRef.value.reset()
      template.value = new CanalWhatsAppTemplateModel()
      tempIdEdicao.value = null
    }

    // --- Ações de Salvar ---

    // 1. Salvar Local (Memória)
    function saveLocal() {
      if (tempIdEdicao.value) {
        // Atualizar existente
        const index = templatesLocais.value.findIndex(t => t.tempId === tempIdEdicao.value)
        if (index !== -1) {
          const atualizado = { ...template.value, tempId: tempIdEdicao.value }
          templatesLocais.value.splice(index, 1, atualizado)
        }
      } else {
        // Criar novo com ID temporário
        const novo = { ...template.value, tempId: Date.now() }
        templatesLocais.value.push(novo)
      }
      // Emite para o pai
      emit('update:templates', templatesLocais.value)
      resetData()
    }

    // 2. Salvar API (Legado)
    async function createTemplateApi() {
      try {
        loading.value = true
        await CanalService.createTemplateWhatsApp(props.canalWhatsAppId, template.value)
        loading.value = false
        resetData()
        await getTemplatesCadastrados()
      } catch {
        loading.value = false
      }
    }

    async function updateTemplateApi() {
      try {
        loading.value = true
        await CanalService.updateTemplateWhatsApp(props.canalWhatsAppId, template.value.id, template.value)
        loading.value = false
        resetData()
        await getTemplatesCadastrados()
      } catch {
        loading.value = false
      }
    }

    async function handleSubmit() {
      if (!formRef.value.validate()) {
        return Vue.$toast.warning('Preencha corretamente os campos antes de salvar')
      }

      if (isModoLocal.value) {
        saveLocal()
      } else {
        if (loading.value) return
        if (isNovoTemplate.value) await createTemplateApi()
        else await updateTemplateApi()
      }
    }

    // --- Utils Download ---
    const templateSelecionadoUrl = ref(null)
    const templateSelecionadoRef = ref(null)

    function downloadTemplateSelecionado(conteudo) {
      if (!conteudo) return
      const blob = new Blob([conteudo], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      templateSelecionadoUrl.value = url
      setTimeout(() => {
        templateSelecionadoRef.value.click()
        URL.revokeObjectURL(url)
      }, 10)
    }

    const headers = [
      { text: 'Nome', value: 'nome' },
      { text: 'Zenvia ID', value: 'zenviaTemplateId' },
      { text: 'Template', value: 'conteudo' },
      { text: 'Data alteração', value: 'dataUltimaAlteracao' },
      { text: 'Status', value: 'ativo' },
    ]
    const templateExemploUrl = 'https://vikdocumentdb.blob.core.windows.net/utils/TemplateCanalWhatsApp.txt?sv=2021-10-04&st=2024-08-16T13%3A09%3A44Z&se=2032-08-17T13%3A09%3A00Z&sr=b&sp=r&sig=EoYJ36puMc8QmVezqVtblHH%2F06MnTL725zfm%2Bp1Nr1A%3D'

    return {
      // Data
      templatesCadastrados,
      templatesLocais,
      listaTemplatesExibicao,
      isGetTemplatesLoading,
      template,
      formRef,
      loading,
      templateInputRef,
      templateSelecionadoRef,
      templateSelecionadoUrl,
      // Computed
      tituloBotaoSubmit,
      fileInputRules,
      // Methods
      uploadTemplate,
      selecionarTemplate,
      resetData,
      handleSubmit,
      downloadTemplateSelecionado,
      // Consts
      validators: { required, select },
      icons: { mdiPaperclip, mdiDownloadCircle },
      functions: { converterDateStringEmLocaleDateString, truncateText },
      headers,
      templateExemploUrl,
    }
  },
}
</script>
